import { useEffect, useRef, useState } from 'react';
import { normaliseIndianMobile, type CustomerDetails } from '@sp/core';
import { api } from '../lib/api';
import type { Customer } from '../lib/types';
import { maskPhone } from '../lib/format';
import { useAuth } from '../store/auth';
import { useTranslation } from '../i18n';
import { Modal } from './ui';
import { CustomerFields } from './CustomerFields';

interface Props {
  flowId: string;
  current?: Customer | null;
  initialPhone?: string;
  startCreate?: boolean;
  canCreate: boolean;
  fullDetails?: boolean;
  onSelect: (customer: Customer) => void;
  onRemove?: () => void;
  onClose: () => void;
}

/** Caller-owned: never imports or mutates the cart. Closing invalidates every pending callback. */
export function CustomerPickerDrawer({
  flowId,
  current,
  initialPhone,
  startCreate = false,
  canCreate,
  fullDetails,
  onSelect,
  onRemove,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const principal = useAuth((s) => s.principal);
  const session = `${principal?.userId}:${principal?.storeId}:${principal?.terminalId}`;
  const context = useRef({ session, flowId });
  context.current = { session, flowId };
  const epoch = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  const [term, setTerm] = useState(initialPhone ?? '');
  const [rows, setRows] = useState<Customer[]>([]);
  const [creating, setCreating] = useState(startCreate);
  const [details, setDetails] = useState(false);
  const [name, setName] = useState('');
  const [fields, setFields] = useState<Partial<CustomerDetails>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      epoch.current++;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    epoch.current++;
    controller.current?.abort();
    setRows([]);
    setBusy(false);
  }, [flowId, session]);
  /**
   * A lookup that finds nobody is the cue to write the customer down: the cursor jumps straight
   * to the Name box so the cashier can type without reaching for the mouse.
   */
  const nameRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!creating) return;
    const raf = window.requestAnimationFrame(() => nameRef.current?.focus());
    return () => window.cancelAnimationFrame(raf);
  }, [creating]);
  function request() {
    controller.current?.abort();
    controller.current = new AbortController();
    const n = ++epoch.current;
    const key = { ...context.current };
    return {
      signal: controller.current.signal,
      valid: () => {
        const p = useAuth.getState().principal;
        const authenticatedSession = `${p?.userId}:${p?.storeId}:${p?.terminalId}`;
        return (
          alive.current &&
          epoch.current === n &&
          context.current.flowId === key.flowId &&
          authenticatedSession === key.session
        );
      },
    };
  }
  function cancel() {
    epoch.current++;
    controller.current?.abort();
    onClose();
  }
  async function search() {
    if (!term.trim()) return;
    const ticket = request();
    setBusy(true);
    setError(null);
    setMessage(null);
    setCreating(false);
    try {
      const result = await api.get<{ customers: Customer[] }>(
        '/api/customers/lookup',
        { q: normaliseIndianMobile(term) ?? term.trim() },
        ticket.signal,
      );
      if (!ticket.valid()) return;
      setRows(result.customers);
      setCreating(result.customers.length === 0);
      if (!result.customers.length) setMessage(t('missing'));
    } catch (e) {
      if (ticket.valid()) setError(e instanceof Error ? e.message : t('lookupFailed'));
    } finally {
      if (ticket.valid()) setBusy(false);
    }
  }
  useEffect(() => {
    if (initialPhone) void search();
  }, []);
  async function save() {
    const phone = normaliseIndianMobile(term);
    if (!phone) {
      setError(t('invalidMobile'));
      return;
    }
    if (!name.trim()) {
      setError(t('invalidName'));
      return;
    }
    const ticket = request();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ customer: Customer; created: boolean }>(
        '/api/customers',
        { ...fields, name: name.trim(), phone },
        ticket.signal,
      );
      if (!ticket.valid()) return;
      if (result.created) onSelect(result.customer);
      else {
        setRows([result.customer]);
        setCreating(false);
        setMessage(t('duplicate'));
      }
    } catch (e) {
      if (ticket.valid()) setError(e instanceof Error ? e.message : t('requestFailed'));
    } finally {
      if (ticket.valid()) setBusy(false);
    }
  }
  return (
    <Modal
      title={t('choose')}
      subtitle={t('search')}
      onClose={cancel}
      closeLabel={t('cancel')}
      placement="sheet"
      testId="customer-picker"
      restoreFocus="previous"
    >
      {current ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3">
          <span className="min-w-0 flex-1 break-words">
            {t('current')}: {current.name} · {maskPhone(current.phone)}
          </span>
          {onRemove ? (
            <button
              className="btn min-h-11"
              onClick={() => {
                epoch.current++;
                onRemove();
              }}
            >
              {t('remove')}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex gap-2">
        <input
          className="field min-w-0"
          data-testid={startCreate ? 'customer-new-phone' : 'pos-customer-phone'}
          aria-label={t('phone')}
          // Opened to create, the phone is already known: the name box takes the cursor instead.
          {...(startCreate ? {} : { 'data-autofocus': '' })}
          placeholder={t('search')}
          value={term}
          onChange={(e) => {
            epoch.current++;
            controller.current?.abort();
            setTerm(e.target.value);
            setRows([]);
            setBusy(false);
            setMessage(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void search();
            }
          }}
        />
        <button
          className="btn min-h-11"
          data-testid="pos-customer-find"
          disabled={busy || !term.trim()}
          onClick={() => void search()}
        >
          {t('find')}
        </button>
      </div>
      {error ? (
        <p className="mt-3 text-red-700" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="mt-3 text-sm" role="status">
          {message}
        </p>
      ) : null}
      <div className="mt-3 space-y-3">
        {rows.map((customer) => (
          <div
            key={customer.id}
            className="rounded-xl border border-slate-200 p-3"
            data-testid="pos-customer-found"
          >
            <p className="break-words font-semibold">{customer.name}</p>
            <p className="text-sm">
              {maskPhone(customer.phone)}
              {customer.isActive === false ? ` · ${t('inactive')}` : ''}
            </p>
            <button
              className="btn btn-primary mt-2 min-h-11 w-full whitespace-normal"
              data-testid="pos-customer-attach"
              onClick={() => {
                epoch.current++;
                onSelect(customer);
              }}
            >
              {t('select')}
            </button>
          </div>
        ))}
      </div>
      {canCreate && !creating ? (
        <button className="btn mt-3 min-h-11 w-full" onClick={() => setCreating(true)}>
          {t('newCustomer')}
        </button>
      ) : null}
      {canCreate && creating ? (
        <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
          <label className="field-label">
            {t('name')}
            <input
              ref={nameRef}
              className="field"
              aria-label={t('customerName')}
              data-testid="customer-new-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </label>
          {fullDetails ? (
            <>
              <button className="btn min-h-11" onClick={() => setDetails((v) => !v)} aria-expanded={details}>
                {t('details')}
              </button>
              {details ? <CustomerFields value={fields} onChange={setFields} /> : null}
            </>
          ) : null}
          <button
            className="btn btn-primary min-h-11 w-full whitespace-normal"
            data-testid="customer-new-submit"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? t('saving') : t('create')}
          </button>
        </div>
      ) : null}
    </Modal>
  );
}
